import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const TRACK_LABELS = { managed_service: 'Managed', saas: 'SaaS', internal: 'Internal' };
const TRACK_COLOURS = {
  managed_service: { bg: '#FFF5F5', text: '#8B1A1A', border: '#FED7D7' },
  saas: { bg: '#EBF8FF', text: '#2B6CB0', border: '#BEE3F8' },
  internal: { bg: '#F7FAFC', text: '#4A5568', border: '#E2E8F0' },
};
const STATUS_COLOURS = {
  active: { bg: '#F0FFF4', text: '#276749' },
  suspended: { bg: '#FFFAF0', text: '#9C4221' },
  cancelled: { bg: '#FFF5F5', text: '#822727' },
};

export default function AccountsList() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTrack, setFilterTrack] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', plan_track: 'managed_service', plan_tier: 'partner', billing_email: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API}/accounts`, { headers: { 'Authorization': 'Bearer ' + token } });
      const data = await r.json();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return accounts.filter(a => {
      if (filterTrack !== 'all' && a.plan_track !== filterTrack) return false;
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      return true;
    });
  }, [accounts, filterTrack, filterStatus]);

  async function handleCreate() {
    if (!form.name.trim()) return;
    setCreating(true); setCreateError(null);
    try {
      const r = await fetch(`${API}/accounts`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await getToken()) },
        body: JSON.stringify(form)
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setShowCreate(false);
      setForm({ name: '', plan_track: 'managed_service', plan_tier: 'partner', billing_email: '' });
      await load();
    } catch (e) { setCreateError(e.message); }
    setCreating(false);
  }

  async function handleSetStatus(e, account, newStatus) {
    e.stopPropagation();
    const verb = newStatus === 'active' ? 'reactivate' : 'deactivate';
    const cascade = (account.workspace_count || 0) > 0
      ? ` This will also ${verb} ${account.workspace_count} workspace${account.workspace_count === 1 ? '' : 's'} under this account.`
      : '';
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${account.name}?${cascade}`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API}/accounts/${account.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) {
        const body = await res.text();
        alert(`Status update failed (${res.status}): ${body}`);
        return;
      }
      setAccounts(accounts.map(a => a.id === account.id ? { ...a, status: newStatus } : a));
    } catch (err) { alert('Could not update status: ' + err.message); }
  }

  const tierOptions = form.plan_track === 'managed_service'
    ? [{ v: 'launchpad', l: 'Launchpad' }, { v: 'growth', l: 'Growth' }, { v: 'partner', l: 'Partner' }]
    : form.plan_track === 'saas'
    ? [{ v: 'starter', l: 'Starter' }, { v: 'growth', l: 'Growth' }, { v: 'agency', l: 'Agency' }]
    : [];

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.2rem', color: '#1A1A1A' }}>Accounts</h1>
          <p style={{ margin: 0, fontSize: '0.83rem', color: '#888' }}>Every paying entity — managed service, SaaS, internal.</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          style={{ background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}>
          + New account
        </button>
      </div>

      {showCreate && (
        <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1.5fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Account name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Customer name"
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Plan</label>
              <select value={form.plan_track} onChange={e => setForm({...form, plan_track: e.target.value, plan_tier: e.target.value === 'managed_service' ? 'partner' : e.target.value === 'saas' ? 'starter' : ''})}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                <option value="managed_service">Managed</option>
                <option value="saas">SaaS</option>
                <option value="internal">Internal</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Tier</label>
              <select value={form.plan_tier} onChange={e => setForm({...form, plan_tier: e.target.value})} disabled={form.plan_track === 'internal'}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                {tierOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                {form.plan_track === 'internal' && <option value="">—</option>}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Billing email</label>
              <input value={form.billing_email} onChange={e => setForm({...form, billing_email: e.target.value})} placeholder="finance@company.com"
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
          </div>
          {createError && <p style={{ color: '#C53030', fontSize: '0.83rem', margin: '0 0 0.75rem' }}>{createError}</p>}
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button onClick={handleCreate} disabled={creating || !form.name.trim()}
              style={{ background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', opacity: creating || !form.name.trim() ? 0.5 : 1 }}>
              {creating ? 'Creating...' : 'Create account'}
            </button>
            <button onClick={() => setShowCreate(false)}
              style={{ background: 'none', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <select value={filterTrack} onChange={e => setFilterTrack(e.target.value)}
          style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer' }}>
          <option value="all">All plans</option>
          <option value="managed_service">Managed</option>
          <option value="saas">SaaS</option>
          <option value="internal">Internal</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer' }}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div style={{ height: '60px', background: '#EEECEA', borderRadius: '10px' }} />
      ) : (
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#FAF8F6' }}>
                <th style={{ textAlign: 'left', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Account</th>
                <th style={{ textAlign: 'left', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plan</th>
                <th style={{ textAlign: 'left', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Workspaces</th>
                <th style={{ textAlign: 'right', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Users</th>
                <th style={{ textAlign: 'right', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No accounts match these filters</td></tr>
              )}
              {filtered.map(a => {
                const trackC = TRACK_COLOURS[a.plan_track] || TRACK_COLOURS.internal;
                const statusC = STATUS_COLOURS[a.status] || STATUS_COLOURS.active;
                return (
                  <tr key={a.id} onClick={() => navigate(`/admin/accounts/${a.id}`)}
                    style={{ borderTop: '0.5px solid #F0EDE9', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FAF8F6'}
                    onMouseLeave={e => e.currentTarget.style.background = '#FFF'}>
                    <td style={{ padding: '0.85rem 1rem', color: '#1A1A1A', fontWeight: '500' }}>{a.name}</td>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: '500', color: trackC.text, background: trackC.bg, border: '1px solid ' + trackC.border, padding: '0.2rem 0.55rem', borderRadius: '20px' }}>
                        {TRACK_LABELS[a.plan_track] || a.plan_track}{a.plan_tier ? ' · ' + a.plan_tier : ''}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: '500', color: statusC.text, background: statusC.bg, padding: '0.2rem 0.55rem', borderRadius: '20px' }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: '#555' }}>{a.workspace_count}</td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: '#555' }}>{a.user_count}</td>
                  <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      {(a.status || 'active') === 'active' ? (
                        <button onClick={e => handleSetStatus(e, a, 'inactive')} style={{ fontSize: '0.72rem', color: '#B7791F', background: 'none', border: '1px solid #F6E05E', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Deactivate</button>
                      ) : (
                        <button onClick={e => handleSetStatus(e, a, 'active')} style={{ fontSize: '0.72rem', color: '#276749', background: 'none', border: '1px solid #9AE6B4', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Reactivate</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
